/**
 * Storage Utilities - Debounced localStorage operations to prevent accumulation
 */

// Map to track pending storage operations: { key: { timeoutId, value } }
const pendingWrites = new Map()

/**
 * Debounced localStorage setter to prevent excessive writes
 * @param {string} key - localStorage key
 * @param {any} value - Value to store
 * @param {number} delay - Debounce delay in milliseconds (default: 300ms)
 */
export const debouncedSetItem = (key, value, delay = 300) => {
  // Clear any existing timeout for this key
  if (pendingWrites.has(key)) {
    clearTimeout(pendingWrites.get(key).timeoutId)
  }

  // Set new timeout
  const timeoutId = setTimeout(() => {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(key, stringValue)
      pendingWrites.delete(key)
    } catch (error) {
      // Handle quota exceeded gracefully - this is non-critical UI state
      if (error.name === 'QuotaExceededError') {
        console.warn(`[StorageUtils] localStorage quota exceeded for ${key}, expansion state not saved`)
      } else {
        console.warn(`[StorageUtils] Failed to save ${key} to localStorage:`, error)
      }
      pendingWrites.delete(key)
    }
  }, delay)

  // Store both the timeout ID and the value for flushing
  pendingWrites.set(key, { timeoutId, value })
}

/**
 * Get item from localStorage with error handling
 * @param {string} key - localStorage key
 * @param {any} defaultValue - Default value if key doesn't exist or parsing fails
 * @returns {any} Parsed value or default
 */
export const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    if (item === null) return defaultValue
    
    // Try to parse as JSON, fall back to string if it fails
    try {
      return JSON.parse(item)
    } catch {
      return item
    }
  } catch (error) {
    console.warn(`[StorageUtils] Failed to read ${key} from localStorage:`, error)
    return defaultValue
  }
}

/**
 * Remove item from localStorage with error handling
 * @param {string} key - localStorage key
 */
export const removeItem = (key) => {
  try {
    // Cancel any pending write for this key
    if (pendingWrites.has(key)) {
      clearTimeout(pendingWrites.get(key).timeoutId)
      pendingWrites.delete(key)
    }

    localStorage.removeItem(key)
  } catch (error) {
    console.warn(`[StorageUtils] Failed to remove ${key} from localStorage:`, error)
  }
}

/**
 * Clear all pending writes (useful for cleanup on page unload)
 */
export const clearPendingWrites = () => {
  for (const [, { timeoutId }] of pendingWrites) {
    clearTimeout(timeoutId)
  }
  pendingWrites.clear()
}

/**
 * Force flush all pending writes immediately (uses stored values, not localStorage)
 */
export const flushPendingWrites = () => {
  for (const [key, { timeoutId, value }] of pendingWrites) {
    clearTimeout(timeoutId)
    // Skip invalid values that can't be serialized
    if (value === undefined || typeof value === 'function') {
      console.warn(`[StorageUtils] Cannot flush invalid value for ${key}`)
      continue
    }
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(key, stringValue)
    } catch (error) {
      console.warn(`[StorageUtils] Failed to flush ${key}:`, error)
    }
  }
  pendingWrites.clear()
}

/**
 * Clear all wiki-related localStorage keys (for logout cleanup)
 */
export const clearWikiStorage = () => {
  try {
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith('wiki_space_expanded_') || key.startsWith('wiki_expanded_pages_'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => {
      // Cancel any pending writes
      if (pendingWrites.has(key)) {
        clearTimeout(pendingWrites.get(key).timeoutId)
        pendingWrites.delete(key)
      }
      localStorage.removeItem(key)
    })
  } catch (error) {
    console.warn('[StorageUtils] Failed to clear wiki storage:', error)
  }
}

/**
 * Clear wiki localStorage keys for a specific space (for space deletion cleanup)
 * @param {string} spaceId - The ID of the space being deleted
 */
export const clearWikiSpaceStorage = (spaceId) => {
  if (!spaceId) return
  try {
    const keysToRemove = [
      `wiki_space_expanded_${spaceId}`,
      `wiki_expanded_pages_${spaceId}`
    ]
    keysToRemove.forEach(key => {
      // Cancel any pending writes
      if (pendingWrites.has(key)) {
        clearTimeout(pendingWrites.get(key).timeoutId)
        pendingWrites.delete(key)
      }
      localStorage.removeItem(key)
    })
  } catch (error) {
    console.warn(`[StorageUtils] Failed to clear wiki storage for space ${spaceId}:`, error)
  }
}

/**
 * Clear task table preferences from localStorage (for logout cleanup)
 */
export const clearTaskTableStorage = () => {
  try {
    const key = 'taskTable_visibleColumns'
    // Cancel any pending writes
    if (pendingWrites.has(key)) {
      clearTimeout(pendingWrites.get(key).timeoutId)
      pendingWrites.delete(key)
    }
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('[StorageUtils] Failed to clear task table storage:', error)
  }
}

// Cleanup on page unload to prevent memory leaks
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flushPendingWrites()
  })
}

export default {
  debouncedSetItem,
  getItem,
  removeItem,
  clearPendingWrites,
  flushPendingWrites,
  clearWikiStorage,
  clearWikiSpaceStorage,
  clearTaskTableStorage
}
